import { Analytics } from '@util/Analytics';
import React, { ReactElement, useEffect, useState } from 'react';
import { LayoutScreenConfig, ScreenType } from '../../config/FlowTypes';
import { ActionType } from '../../store/ActionTypes';
import { AppSelector, useAppDispatch, useAppStore } from '../../store/Store';
import { NavigationProps } from './FlowNavigation';
import {
  AuthLayoutConfig,
  ForgotPasswordContainer,
  LoginContainer,
  PageContainer,
  RegisterContainer,
} from './layouts/AuthLayout';

export type AuthScreenConfig<ScreenNames extends string> = LayoutScreenConfig<
  ScreenType.Auth,
  AuthLayoutConfig,
  ScreenNames
>;

type Props<ScreenNames extends string> = NavigationProps<
  AuthScreenConfig<ScreenNames>,
  ScreenNames
>;

type AuthView = 'login' | 'register' | 'forgotPassword';

export const AuthScreen = <ScreenNames extends string>(props: Props<ScreenNames>): ReactElement => {
  const { navigation, layout } = props;
  const pageStack = useAppStore(AppSelector.pageStack);
  const cameFromFirstScreen = pageStack.length === 2;
  const [view, setView] = useState<AuthView>(
    cameFromFirstScreen || layout.defaultsToLogin ? 'login' : 'register'
  );
  const [prefillEmail, setPrefillEmail] = useState<string>('');
  const dispatch = useAppDispatch();
  const user = useAppStore(AppSelector.user);

  useEffect(
    function trackAuthOpened() {
      if (view === 'login') {
        Analytics.track({ event: Analytics.Event.a_login_opened });
      } else if (view === 'register') {
        Analytics.track({ event: Analytics.Event.a_signup_opened });
      }
    },
    [view]
  );

  useEffect(
    function navigateAuthenticatedUser() {
      if (user.userId !== undefined) {
        // Skip writing to userData when auto-navigating; user did not answer on this screen
        navigation.next(props, { resetHistory: true });
      }
    },
    [props, navigation, user.userId]
  );

  function switchView(newView: AuthView) {
    dispatch({ type: ActionType.CLEAR_USER_ERROR });
    setView(newView);
  }

  function renderContent() {
    if (view === 'login') {
      return (
        <LoginContainer
          loginTitle={layout.loginTitle}
          loginButtonTitle={layout.loginButtonTitle}
          onRegister={
            cameFromFirstScreen ? () => navigation.goBack() : () => switchView('register')
          }
          onForgotPassword={(email) => {
            setPrefillEmail(email);
            switchView('forgotPassword');
          }}
        />
      );
    } else if (view === 'forgotPassword') {
      return (
        <ForgotPasswordContainer
          initialEmail={prefillEmail}
          onBackToLogin={() => switchView('login')}
        />
      );
    } else {
      return (
        <RegisterContainer
          firstName={user?.name}
          registerTitle={layout.registerTitle}
          registerBody={layout.registerBody}
          registerButtonTitle={layout.registerButtonTitle}
          onLogin={() => switchView('login')}
          registerPrivacyDeclaration={layout.registerPrivacyDeclaration}
          privacyLogos={layout.privacyLogos}
        />
      );
    }
  }

  return <PageContainer>{renderContent()}</PageContainer>;
};
